import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { Customer } from '../../types/customers';
import type { PricePlan } from '../../types/pricePlans';
import type { Lead } from '../../types';
import { getLeadsWithOpportunities } from '../../api/leads';
import { getPricePlans } from '../../api/pricePlans';
import { addCustomer } from '../../api/customers';
import { API_STALE_TIME_MS } from '../../constants/api';
import type { Contact } from '../../types/common';
import type { Address } from '../../types/customers';
import AddressesFormSection from '../common/AddressesFormSection';
import ContactsFormSection from '../common/ContactsFormSection';
import { Typography, Divider } from 'antd';
import LanguageSelector from '../common/LanguageSelector';
import CurrencySelector from '../common/CurrencySelector';

interface CustomerModalProps {
  open: boolean;
  customer?: Customer | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface WorkspaceFormData {
  workspace_name: string;
  workspace_id: string;
  language: string;
  currency: string;
  price_plan_id?: string;
}

interface FormData {
  property_name: string;
  workspace: WorkspaceFormData;
  addresses: Address[];
  description?: string;
  notes?: string;
  value?: number;
  units?: number;
  contacts: Contact[];
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  open,
  customer,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['sales', 'common']);
  const [form] = Form.useForm<FormData>();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Fetch opportunities for dropdown
  const { data: leadsWithOpportunities = [], isLoading: isLoadingLeads } = useQuery<Lead[]>({
    queryKey: ['leadsWithOpportunities'],
    queryFn: getLeadsWithOpportunities,
    staleTime: API_STALE_TIME_MS,
    enabled: open,
  });

  // Fetch price plans for dropdown
  const { data: pricePlansData = { pricePlans: [] } } = useQuery<{ pricePlans: PricePlan[] }>({
    queryKey: ['pricePlans'],
    queryFn: async () => {
      return getPricePlans().then(res => ({ pricePlans: res.pricePlans }));
    },
    staleTime: 5 * 60 * 1000,
  });
  const pricePlans: PricePlan[] = pricePlansData.pricePlans || [];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      form.resetFields();
      if (customer) {
        const formValues = {
          property_name: customer.property_name,
          workspace: {
            workspace_name: customer.workspace_name || '',
            workspace_id: customer.workspace_slug || '',
            language: '',
            currency: '',
            price_plan_id: customer.price_plan_id,
          },
          addresses: customer.addresses ? [...customer.addresses] : [],
          contacts: customer.contacts || [],
          description: customer.description,
          notes: customer.notes,
          value: customer.value,
          units: customer.units,
        };
        form.setFieldsValue(formValues);
      } else {
        form.setFieldsValue({
          addresses: [],
          contacts: [],
        });
      }
    }
  }, [open, form, customer]);

  // Auto-populate fields when opportunity is selected
  useEffect(() => {
    if (selectedLead) {
      const workspaceName = `${selectedLead.name} Workspace`;
      const workspaceSlug = workspaceName.toLowerCase().replace(/\s+/g, '-');
      form.setFieldsValue({
        property_name: selectedLead.name,
        workspace: {
          workspace_name: workspaceName,
          workspace_id: workspaceSlug,
          language: '',
          currency: '',
          price_plan_id: undefined,
        },
        addresses: [
          {
            type: 'Property',
            address: selectedLead.address,
            city: selectedLead.city,
            pincode: selectedLead.pincode,
            country: selectedLead.country,
          },
        ],
        description: selectedLead.description,
        notes: selectedLead.notes,
        value: selectedLead.opportunity?.value?.amount || undefined,
        units: selectedLead.opportunity?.units || undefined,
        contacts: selectedLead.contacts || [],
      });
    }
  }, [selectedLead, form]);

  const handleOpportunityChange = useCallback((leadId: string) => {
    const lead = leadsWithOpportunities.find(lead => lead.id === leadId) || null;
    setSelectedLead(lead);
  }, [leadsWithOpportunities]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const newCustomer: Omit<Customer, 'id'> = {
        property_name: values.property_name,
        price_plan_id: values.workspace.price_plan_id || '',
        workspace_id: values.workspace.workspace_id,
        workspace_name: values.workspace.workspace_name,
        workspace_slug: values.workspace.workspace_id,
        status: 'Active',
        created_at: new Date(),
        updated_at: new Date(),
        billing_cycle: 'monthly',
        billing_start_date: new Date(),
        payment_method: 'credit_card',
        invoices: [],
        contacts: values.contacts || [],
        primary_contact: values.contacts && values.contacts.length > 0 ? values.contacts[0] : {
          name: '',
          email: '',
          phone: '',
          position: '',
          type: 'Primary' as const,
          value: '',
        },
        addresses: values.addresses || [],
        description: values.description,
        notes: values.notes,
        value: values.value,
        units: values.units,
        price_plan_name: pricePlans.find((plan: PricePlan) => plan.id === values.workspace.price_plan_id)?.name || '',
      };
      await addCustomer(newCustomer);
      message.success(t('sales:success.createSuccess', { entity: t('customer') }));
      onSuccess();
    } catch (error) {
      console.error('Error creating customer:', error);
      message.error(t('sales:errors.createFailed', { entity: t('customer') }));
    }
  }, [form, onSuccess, t]);

  return (
    <Modal
      title={customer ? t('sales:customers.edit') : t('sales:customers.add')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={800}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Typography.Title level={5}>{t('sales:customers.detailsSection', 'Customer Details')}</Typography.Title>
        {!customer && (
          <>
            <Form.Item
              name="opportunity_id"
              label={t('sales:customers.fields.opportunity')}
              rules={[{ required: true, message: t('common:validation.required') }]}
            >
              <Select
                placeholder={t('sales:customers.fields.selectOpportunity')}
                loading={isLoadingLeads}
                onChange={handleOpportunityChange}
                options={leadsWithOpportunities.map(lead => ({
                  label: lead.name,
                  value: lead.id,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="lead_id"
              hidden
            >
              <Input />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="property_name"
          label={t('sales:customers.fields.propertyName')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Input />
        </Form.Item>
        {/* Workspace Section */}
        <Divider />
        <Typography.Title level={5}>{t('sales:customers.workspace', 'Workspace')}</Typography.Title>
        <Form.Item
          name={["workspace", "workspace_name"]}
          label={t('sales:customers.fields.workspaceName')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={["workspace", "workspace_id"]}
          label={t('sales:customers.fields.workspaceSlug')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={["workspace", "language"]}
          label={t('sales:customers.fields.language', 'Language')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <LanguageSelector />
        </Form.Item>
        <Form.Item
          name={["workspace", "currency"]}
          label={t('sales:customers.fields.currency', 'Currency')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <CurrencySelector />
        </Form.Item>
        <Form.Item
          name={["workspace", "price_plan_id"]}
          label={t('sales:customers.fields.pricePlan')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Select
            placeholder={t('sales:customers.fields.selectPricePlan')}
            allowClear
            options={pricePlans.map((plan: PricePlan) => ({
              label: plan.name,
              value: plan.id,
            }))}
          />
        </Form.Item>
        <Divider />
        <Typography.Title level={5}>{t('common:addresses', 'Addresses')}</Typography.Title>
        <AddressesFormSection t={t} initialValues={form.getFieldValue('addresses')} />
        <Divider />
        <Typography.Title level={5}>{t('common:contacts', 'Contacts')}</Typography.Title>
        <ContactsFormSection t={t} contactTypeOptions={[
          { value: 'Primary', label: 'Primary' },
          { value: 'Secondary', label: 'Secondary' },
          { value: 'Decision Maker', label: 'Decision Maker' },
        ]} />
        <Divider />
        <Typography.Title level={5}>{t('sales:opportunities.infoSection', 'Opportunity Info')}</Typography.Title>
        <Form.Item
          name="value"
          label={t('sales:opportunities.fields.value', 'Value')}
        >
          <Input type="number" />
        </Form.Item>
        <Form.Item
          name="units"
          label={t('sales:opportunities.fields.units', 'Units')}
        >
          <Input type="number" />
        </Form.Item>
        <Divider />
        <Typography.Title level={5}>{t('common:otherInfo', 'Other Info')}</Typography.Title>
        <Form.Item
          name="description"
          label={t('common:description')}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="notes"
          label={t('common:notes')}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.ErrorList />
      </Form>
    </Modal>
  );
};

export default CustomerModal; 